/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Responsive variants of the site's page images — one definition, three
 * consumers, like staticRoutes.js: plain ESM with zero imports because the
 * consumers cannot share TypeScript.
 *   1. the components (Hero, PageHero, the content images) build `srcset` /
 *      `sizes` from it;
 *   2. server/routes/imageVariants.ts serves exactly these widths and nothing
 *      else — a width that is not in the list is answered with the original;
 *   3. server/lcpPreload.ts emits the hero preload with the SAME candidate
 *      list, otherwise the browser preloads one file and then fetches another.
 *
 * The variant URL is the original with `?w=<width>`: the original address
 * keeps working unchanged (every link, every CMS slot, every old bake), and a
 * server without the resizer simply serves the original for every candidate.
 * Admin uploads live on each environment's disk and were uploaded at whatever
 * size the photo came in — the production homepage hero is a 360 KB file and
 * the contact photo 3 MB, both sent in full to every phone until 2026-10-01 —
 * so the variants have to be made on request, not at build time.
 */

/**
 * Candidate widths, ascending. 480/768 cover phones at 1× and 2× for a
 * backdrop sized at ~60 vw (see IMAGE_SIZES), 1024/1536 tablets and laptops,
 * 2048 a large 2× desktop. A source narrower than a candidate is served at
 * its own width (never enlarged); the browser then picks the next one down
 * next time it has the choice, which is what happens today anyway.
 */
export const IMAGE_VARIANT_WIDTHS = Object.freeze([480, 768, 1024, 1536, 2048]);

/** The query parameter that selects a variant. */
export const IMAGE_VARIANT_PARAM = 'w';

/**
 * `sizes` presets — how wide the image is rendered, so the browser can pick
 * the smallest candidate that still fills it at the device's pixel ratio.
 *
 * `backdrop` deliberately under-reports on phones: every backdrop on the
 * site is drawn at 15–55 % opacity under a vignette or a blend, so a
 * candidate for 60 vw (a 768 px file on a 3× phone instead of a 1536 px one)
 * is indistinguishable there and a third of the bytes.
 */
export const IMAGE_SIZES = Object.freeze({
  /** Full-bleed, low-opacity photo behind a hero or a section. */
  backdrop: '(max-width: 767px) 60vw, 100vw',
  /** One column of a two-column split from lg, full width below. */
  half: '(min-width: 1024px) 50vw, 100vw',
  /** One column of a three-column split from lg, full width below. */
  third: '(min-width: 1024px) 33vw, 100vw',
  /** A card in the md:2 / lg:3 grids (kennisbank, related articles, mid-check). */
  card: '(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw',
  /** A blurred, contained illustration capped at the max-w-5xl column. */
  illustration: '(min-width: 1280px) 1024px, (max-width: 767px) 60vw, 100vw',
});

/**
 * Only the site's own raster images are resizable: the seeded page images
 * and the admin uploads, by their public paths. Logos (`/logos/…`) are small
 * and often transparent PNGs, SVG/GIF/AVIF are not re-encoded, and anything
 * external, inline (`data:`) or already carrying a query is left alone.
 */
const RESIZABLE_RE = /^\/(?:images|uploads)\/[^?#]+\.(?:webp|png|jpe?g)$/i;

/**
 * @param {unknown} src
 * @returns {src is string}
 */
export const isResizableImageSrc = (src) => typeof src === 'string' && RESIZABLE_RE.test(src);

/**
 * @param {string} src a resizable source
 * @param {number} width one of IMAGE_VARIANT_WIDTHS
 */
export const imageVariantUrl = (src, width) => `${src}?${IMAGE_VARIANT_PARAM}=${width}`;

/**
 * The `srcset` value for a resizable source; `''` for anything else so a
 * caller can spread it conditionally.
 * @param {unknown} src
 */
export const imageSrcSet = (src) =>
  isResizableImageSrc(src)
    ? IMAGE_VARIANT_WIDTHS.map((w) => `${imageVariantUrl(src, w)} ${w}w`).join(', ')
    : '';

/**
 * The `<img>` props for a source: `srcSet` + `sizes` when it can be resized,
 * the bare `src` when it cannot (an external URL keeps working as before).
 * @param {string} src
 * @param {string} sizes one of IMAGE_SIZES, or a custom sizes string
 * @returns {{ src: string, srcSet?: string, sizes?: string }}
 */
export const responsiveImageProps = (src, sizes) =>
  isResizableImageSrc(src) ? { src, srcSet: imageSrcSet(src), sizes } : { src };
